import BScroll from '@better-scroll/core';
import site from '../site';
import fastdom from "fastdom";

export default class IconNavigation {

	private container: HTMLDivElement;
	private scrollInstance;
	private scrollInner: HTMLDivElement;
	private scrollLinks: NodeListOf<HTMLLinkElement>;
	private scrollList: HTMLDivElement;
	private scrollItemActive;
	private scrollArrowLeft;
	private scrollArrowRight;
	private scrollLinkTriggered = false;

	private hideLeftClass = 'c-icon-navigation--hide-left-arrow';
	private hideRightClass = 'c-icon-navigation--hide-right-arrow';

	constructor(container: HTMLDivElement) {

		this.container = container;
		this.scrollInner = this.container.querySelector('.js-icon-navigation-inner') as HTMLDivElement;
		this.scrollLinks = this.container.querySelectorAll('.js-icon-navigation-link') as NodeListOf<HTMLLinkElement>;
		this.scrollList = this.container.querySelector('.js-icon-navigation-list') as HTMLDivElement;
		this.scrollItemActive = this.container.querySelector('.js-icon-navigation-item-active');

		this.checkIfActiveLinkShouldBeSet();
		this.setup();

		const context = this;
		const debouncedConstrainAndAlign = site.debounce(context.constrainAndAlign.bind(context), 150, null);
		window.addEventListener('resize', debouncedConstrainAndAlign);

	}

	private checkIfActiveLinkShouldBeSet() {
		if (this.container.querySelector('.js-icon-navigation-item-active')) {
			return;
		}
		const pathname = window.location.pathname;
		for (let i = 0; i < this.scrollLinks.length; i++) {
			const link = this.scrollLinks[i] as HTMLLinkElement;
			const linkPathname = new URL(link.href).pathname;
			if (linkPathname === pathname) {
				this.scrollItemActive = this.scrollLinks[i].parentElement;
				this.scrollItemActive.classList.add('c-icon-navigation__item--active');
				this.scrollItemActive.classList.add('js-icon-navigation-item-active');
				this.scrollLinkTriggered = false;
				break;
			}
		}
	}

	private setup() {

		this.scrollInner.style.height = this.scrollList.offsetHeight + 'px';

		this.scrollInstance = new BScroll(this.scrollInner, {
			scrollX: true,
			scrollY: false,
			eventPassthrough: 'vertical',
			scrollbars: false,
			tap: true,
			click: true,
			bounce: true
		});

		this.scrollInstance.on('scrollEnd', async ()=> this.setState());
		this.scrollInstance.on('scrollStart', async() => this.setState());

		this.links();

		this.arrows();

		this.constrainAndAlign();

	}

	private async setState() {

		const currentX = await this.currentX();
		const maxScroll = await this.maxScroll();

		fastdom.mutate(() => {
			if (currentX === 0) {
				this.container.classList.add(this.hideLeftClass);
			} else {
				this.container.classList.remove(this.hideLeftClass);
			}
			if (currentX === maxScroll) {
				this.container.classList.add(this.hideRightClass);
			} else {
				this.container.classList.remove(this.hideRightClass);
			}
		});

	}

	/**
	 * Current X is sometimes 0 on initial load, so we need to retry a few times
	 */
	private async currentX(retries = 3, timeout = 100) {
		const value = Math.abs(this.scrollInstance.x);
		if (retries > 0 && value === 0) {
			return new Promise(resolve => {
				setTimeout(async () => {
					resolve(await this.currentX(value, retries - 1));
				}, timeout);
			});
		} else if (value > 0) {
			return value;
		} else {
			return 0;
		}
	}

	/**
	 * Max scroll is sometimes 0 on initial load, so we need to retry a few times
	 */
	private async maxScroll(retries = 3, timeout = 1000) {
		const value = Math.abs(this.scrollInstance.maxScrollX);
		if (retries > 0 && value === 0) {
			return new Promise(resolve => {
				setTimeout(async () => {
					resolve(await this.maxScroll(value, retries - 1));
				}, timeout);
			});
		} else if (value > 0) {
			return value;
		} else {
			return 0;
		}
	}

	private links() {

		if (!this.scrollLinks.length) return;

		for (let i = 0; i < this.scrollLinks.length; i++) {


			this.scrollLinks[i].addEventListener('keydown', (e) => {

				const keyPressed = site.getKeyPressed(e);
				if (keyPressed === 'Enter' || keyPressed === 13) this.triggerLink(this.scrollLinks[i] as HTMLLinkElement);

			});

			this.scrollLinks[i].addEventListener('tap', () => {

				this.triggerLink(this.scrollLinks[i] as HTMLLinkElement);

			}, false);

			this.scrollLinks[i].addEventListener('focus', (e) => {

				setTimeout(function () {

					if (this.scrollInstance && !this.scrollLinkTriggered) this.scrollInstance.scrollToElement(e.target, 100, true, true);

				}, 100);

			});

		}

	}

	private createArrow(classes: string[], text: string) {

		let button = document.createElement('button');
		for (let i = 0; i < classes.length; i++) { button.classList.add(classes[i]); }
		button.setAttribute('type', 'button');
		button.setAttribute('aria-label', text);
		button.setAttribute('tabindex', "-1");
		button.innerHTML = text;
		return button;

	}

	private arrows() {

		const arrowLeftElement = this.createArrow(['c-icon-navigation__arrow', 'c-icon-navigation__arrow--left', 'js-icon-navigation-left'], 'Scroll left');
		const arrowRightElement = this.createArrow(['c-icon-navigation__arrow', 'c-icon-navigation__arrow--right', 'js-icon-navigation-right'], 'Scroll right');

		this.scrollInner.appendChild(arrowLeftElement);
		this.scrollInner.appendChild(arrowRightElement);

		this.scrollArrowLeft = this.container.querySelector('.js-icon-navigation-left');
		this.scrollArrowRight = this.container.querySelector('.js-icon-navigation-right');

		this.scrollArrowLeft.addEventListener('click', (e) => {

			if (!this.scrollInstance) return;
			this.scrollInstance.scrollBy(this.scrollBy(false), 0, 250);

		});

		this.scrollArrowRight.addEventListener('click', (e) => {

			if (!this.scrollInstance) return;
			this.scrollInstance.scrollBy(this.scrollBy(true), 0, 250);

		});

	}

	private async constrainAndAlign() {

		if (!this.scrollInner || !this.scrollList) return;

		this.scrollInner.style.height = this.scrollList.offsetHeight + 'px';

		this.container.classList.remove('c-icon-navigation--can-navigate');

		if (this.scrollList.offsetWidth > this.scrollInner.offsetWidth) {

			this.container.classList.add('c-icon-navigation--can-navigate');
			this.scrollToActive();

		}

		if (this.scrollInstance) setTimeout(() => this.scrollInstance.refresh(), 0);
		this.container.classList.add('c-icon-navigation--show');

		await this.setState();

	}

	private scrollToActive() {

		if (!this.scrollItemActive || !this.scrollInstance) return;

		setTimeout(() => this.scrollInstance.scrollToElement(this.scrollItemActive, 100, true, true), 100);
	}

	private isLarge() {

		return window.matchMedia('(min-width: 980px)').matches;

	}

	private scrollBy(negative: boolean) {

		let amount = this.isLarge() ? 160 : 90; // .c-icon-navigation__item outer width
		if (negative) amount = - Math.abs(amount);
		return amount;

	}

	private triggerLink(link: HTMLLinkElement) {

		this.scrollLinkTriggered = true;

		const url = link.getAttribute('href');
		window.location.href = url;

	}

}
